import { Search, X, SlidersHorizontal, ChevronDown } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import type { Priority } from '../../types';
import { useApp } from '../../context/AppContext';
import { getPriorityConfig, getStatusConfig } from '../../utils/helpers';
import Avatar from '../common/Avatar';

export interface BoardFilters {
  search: string;
  status: string[];
  priority: Priority[];
  assigneeIds: string[];
  labelIds: string[];
}

export const defaultBoardFilters: BoardFilters = {
  search: '',
  status: [],
  priority: [],
  assigneeIds: [],
  labelIds: [],
};

interface BoardFiltersProps {
  filters: BoardFilters;
  onChange: (filters: BoardFilters) => void;
  allStatuses: string[];
}

const priorities: Priority[] = ['low', 'medium', 'high', 'critical'];

export default function BoardFilters({ filters, onChange, allStatuses }: BoardFiltersProps) {
  const { state } = useApp();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowUserDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const activeFilterCount = [
    filters.status.length > 0,
    filters.priority.length > 0,
    filters.assigneeIds.length > 0,
    filters.labelIds.length > 0,
  ].filter(Boolean).length;

  const update = (partial: Partial<BoardFilters>) => {
    onChange({ ...filters, ...partial });
  };

  const toggleArrayFilter = <K extends keyof BoardFilters>(
    key: K,
    value: string
  ) => {
    const current = filters[key] as string[];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    update({ [key]: next } as Partial<BoardFilters>);
  };

  const clearAll = () => onChange(defaultBoardFilters);

  const selectedUsers = state.users.filter((u) => filters.assigneeIds.includes(u.id));

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={filters.search}
            onChange={(e) => update({ search: e.target.value })}
            placeholder="Buscar tarefas..."
            className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-300 focus:border-brand-400 transition-all placeholder:text-slate-400"
            aria-label="Buscar tarefas"
          />
          {filters.search && (
            <button
              onClick={() => update({ search: '' })}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
              aria-label="Limpar busca"
            >
              <X size={16} />
            </button>
          )}
        </div>

        <div className="flex gap-2">
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowUserDropdown(!showUserDropdown)}
              className={`inline-flex items-center gap-2 px-3 py-2.5 rounded-xl border text-xs font-medium transition-all shrink-0 ${
                showUserDropdown || filters.assigneeIds.length > 0
                  ? 'bg-brand-50 border-brand-200 text-brand-600'
                  : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {filters.assigneeIds.length > 0 ? (
                <>
                  <div className="flex -space-x-1.5">
                    {selectedUsers.slice(0, 2).map((user) => (
                      <Avatar
                        key={user.id}
                        name={user.name}
                        color={user.avatarColor}
                        size="xs"
                      />
                    ))}
                  </div>
                  <span className="hidden sm:inline">
                    {selectedUsers.length === 1
                      ? selectedUsers[0].name.split(' ')[0]
                      : `${selectedUsers.length} pessoas`}
                  </span>
                  {selectedUsers.length > 2 && <span className="hidden sm:inline text-xs">+{selectedUsers.length - 2}</span>}
                </>
              ) : (
                <>
                  <span>Responsáveis</span>
                  <ChevronDown size={14} />
                </>
              )}
            </button>

            {showUserDropdown && (
              <div className="absolute top-full left-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-lg z-50 min-w-56">
                <div className="p-2 max-h-64 overflow-y-auto space-y-1">
                  {state.users.length === 0 ? (
                    <div className="px-3 py-4 text-center text-xs text-slate-400">
                      Nenhum responsável disponível
                    </div>
                  ) : (
                    state.users.map((user) => {
                      const active = filters.assigneeIds.includes(user.id);
                      return (
                        <button
                          key={user.id}
                          onClick={() => {
                            toggleArrayFilter('assigneeIds', user.id);
                          }}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                            active
                              ? 'bg-brand-50 text-brand-600'
                              : 'text-slate-600 hover:bg-slate-50'
                          }`}
                        >
                          <Avatar name={user.name} color={user.avatarColor} size="xs" />
                          <span className="flex-1 text-left">{user.name}</span>
                          {active && (
                            <span className="w-1.5 h-1.5 rounded-full bg-brand-500 shrink-0" />
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>

          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className={`inline-flex items-center gap-2 px-3 py-2.5 rounded-xl border text-xs font-medium transition-all shrink-0 ${
              showAdvanced || (activeFilterCount > 0 && filters.assigneeIds.length === 0)
                ? 'bg-brand-50 border-brand-200 text-brand-600'
                : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            <SlidersHorizontal size={14} />
            <span className="hidden sm:inline">Filtros</span>
            {activeFilterCount - filters.assigneeIds.length > 0 && (
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-brand-500 text-white text-[10px] font-bold">
                {activeFilterCount - filters.assigneeIds.length}
              </span>
            )}
          </button>

          {activeFilterCount > 0 && (
            <button
              onClick={clearAll}
              className="inline-flex items-center gap-1 px-3 py-2.5 rounded-xl border border-red-200 bg-red-50 text-red-600 text-xs font-medium hover:bg-red-100 transition-colors shrink-0"
            >
              <X size={14} />
              <span className="hidden sm:inline">Limpar</span>
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {allStatuses.map((s) => {
          const config = getStatusConfig(s);
          const active = filters.status.includes(s);
          return (
            <button
              key={s}
              onClick={() => toggleArrayFilter('status', s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                active
                  ? `${config.color} border-current shadow-sm`
                  : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
              }`}
            >
              {config.label}
            </button>
          );
        })}
      </div>

      {showAdvanced && (
        <div className="bg-slate-50/80 rounded-2xl border border-slate-200 p-4 space-y-4 animate-fade-in">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <FilterSection label="Prioridade">
              <div className="flex flex-wrap gap-1.5">
                {priorities.map((p) => {
                  const config = getPriorityConfig(p);
                  const active = filters.priority.includes(p);
                  return (
                    <button
                      key={p}
                      onClick={() => toggleArrayFilter('priority', p)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all border ${
                        active
                          ? `${config.color} border-current`
                          : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                      }`}
                    >
                      {config.label}
                    </button>
                  );
                })}
              </div>
            </FilterSection>

            <FilterSection label="Responsável">
              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                {state.users.map((user) => {
                  const active = filters.assigneeIds.includes(user.id);
                  return (
                    <button
                      key={user.id}
                      onClick={() => toggleArrayFilter('assigneeIds', user.id)}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-all border ${
                        active
                          ? 'bg-brand-50 text-brand-600 border-brand-200'
                          : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                      }`}
                    >
                      <Avatar name={user.name} color={user.avatarColor} size="xs" />
                      {user.name.split(' ')[0]}
                    </button>
                  );
                })}
              </div>
            </FilterSection>

            <FilterSection label="Etiquetas">
              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                {state.labels.map((label) => {
                  const active = filters.labelIds.includes(label.id);
                  return (
                    <button
                      key={label.id}
                      onClick={() => toggleArrayFilter('labelIds', label.id)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all border ${
                        active
                          ? 'text-white border-transparent shadow-sm'
                          : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                      }`}
                      style={active ? { backgroundColor: label.color } : undefined}
                    >
                      <span className="flex items-center gap-1.5">
                        <span
                          className="w-2 h-2 rounded-full shrink-0"
                          style={{ backgroundColor: label.color }}
                        />
                        {label.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </FilterSection>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">{label}</p>
      {children}
    </div>
  );
}
